import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, CaretDown, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';

// Generic searchable + addable picker for a master list (categories, uoms…).
// options: [{ id, name }]. `value` is options[].[valueField]. onChange(value). onCreate(name) => created doc.
export default function SearchableMaster({
  value, options = [], onChange, onCreate, canWrite = false,
  valueField = 'id', allowNone = true, testid = 'master', labels = {},
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o[valueField] === value);
  const displayName = selected ? selected.name : (value && valueField === 'name' ? value : '');

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => (
        String(o.name ?? '').toLowerCase().includes(q)
        || String(o.searchable ?? '').toLowerCase().includes(q)
      ))
    : options;
  const exact = options.some((o) => String(o.name ?? '').toLowerCase() === q);

  const pick = (v) => { onChange(v); setOpen(false); setQuery(''); };
  const createNew = async () => {
    const nm = query.trim();
    if (!nm || !onCreate) return;
    setBusy(true);
    try { const c = await onCreate(nm); onChange(c[valueField]); setOpen(false); setQuery(''); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative mt-1" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-select`}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring">
        <span className={displayName ? 'text-zinc-900' : 'text-zinc-400'}>{displayName || t('mfg.none')}</span>
        <CaretDown size={14} className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          <div className="flex items-center gap-1.5 border-b border-zinc-200 px-2.5 py-2">
            <MagnifyingGlass size={14} className="text-zinc-400 shrink-0" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canWrite && q && !exact) { e.preventDefault(); createNew(); } if (e.key === 'Escape') setOpen(false); }}
              placeholder={t(labels.searchPh || 'mfg.category.searchPh')} data-testid={`${testid}-search-input`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-400" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {allowNone && <button type="button" onClick={() => pick('')} className="w-full px-3 py-1.5 text-left text-sm text-zinc-500 hover:bg-zinc-50">{t('mfg.none')}</button>}
            {filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => pick(o[valueField])} data-testid={`${testid}-opt-${o.id}`}
                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-zinc-50 ${o[valueField] === value ? 'bg-orange-50 text-orange-700 font-medium' : 'text-zinc-700'}`}>
                <span className="truncate">{o.name}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {/* どの一覧から来た候補かを出す（社員 / 部門 / 場所マスタ など） */}
                  {o.hint && <span className="text-[11px] text-zinc-400">{o.hint}</span>}
                  {o[valueField] === value && <Check size={14} className="shrink-0" />}
                </span>
              </button>
            ))}
            {filtered.length === 0 && !(canWrite && q && !exact) && <p className="px-3 py-2 text-xs text-zinc-400" data-testid={`${testid}-no-match`}>{t(labels.noMatch || 'mfg.category.noMatch')}</p>}
            {canWrite && q && !exact && onCreate && (
              <button type="button" onClick={createNew} disabled={busy} data-testid={`${testid}-add-confirm`}
                className="flex w-full items-center gap-1.5 border-t border-zinc-200 px-3 py-2 text-left text-sm font-medium text-orange-600 hover:bg-orange-50">
                <Plus size={14} weight="bold" className="shrink-0" /> {t(labels.createNamed || 'mfg.category.createNamed', { name: query.trim() })}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
